import {
    createContext,
    useContext,
    useEffect,
    useState,
} from "react"

const ViewModeContext = createContext()

// Get initial view mode before first render
const getInitialState = () => {
    const appKey = sessionStorage.getItem("app_key")
    
    // Check sessionStorage first (set by applyViewRules)
    const sessionViewMode = sessionStorage.getItem("viewMode")
    const savedView = sessionViewMode || localStorage.getItem("viewMode") || "map_view"

    if (!appKey) {
        return {
            viewMode: savedView,
            canToggle: true,
        }
    }

    const websites = JSON.parse(localStorage.getItem("websites") || "[]")
    const selectedApp = websites.find((w) => w.app_key === appKey)

    if (!selectedApp?.view_type) {
        return {
            viewMode: savedView,
            canToggle: true,
        }
    }

    try {
        const viewArray = JSON.parse(selectedApp.view_type).map((v) =>
            v.toLowerCase()
        )

        if (viewArray.length === 1) {
            const forcedView =
                viewArray[0] === "table" ? "table_view" : "map_view"

            // Only set if different from current
            if (sessionViewMode !== forcedView) {
                localStorage.setItem("viewMode", forcedView)
                sessionStorage.setItem("viewMode", forcedView)
            }

            return {
                viewMode: forcedView,
                canToggle: false,
            }
        }

        return {
            viewMode: savedView,
            canToggle: true,
        }
    } catch {
        return {
            viewMode: savedView,
            canToggle: true,
        }
    }
}

export const ViewModeProvider = ({ children }) => {
    const initialState = getInitialState()

    const [viewMode, setViewMode] = useState(initialState.viewMode)
    const [canToggle, setCanToggle] = useState(initialState.canToggle)

    // MASTER FUNCTION: decides rules for view mode
    const applyViewRules = (appData) => {
        const viewTypeRaw = appData?.view_type

        // Scenario 1 → No view_type → free mode
        if (!viewTypeRaw) {
            setCanToggle(true)
            return
        }

        let viewArray = []

        try {
            viewArray = JSON.parse(viewTypeRaw).map((v) => v.toLowerCase())
        } catch {
            setCanToggle(true)
            return
        }

        // Scenario 3 → Forced mode (single view)
        if (viewArray.length === 1) {
            const forcedView =
                viewArray[0] === "table" ? "table_view" : "map_view"

            setViewMode(forcedView)
            setCanToggle(false)

            localStorage.setItem("viewMode", forcedView)
            sessionStorage.setItem("viewMode", forcedView)

            return
        }

        // Scenario 2 → Both views allowed
        setCanToggle(true)
    }

    const toggleViewMode = () => {
        if (!canToggle) return

        setViewMode((prev) => {
            const updated =
                prev === "map_view" ? "table_view" : "map_view"

            localStorage.setItem("viewMode", updated)
            sessionStorage.setItem("viewMode", updated)

            return updated
        })
    }

    // Re-apply rules when app changes or websites are available
    // Using sessionStorage.getItem("app_key") as a trigger ensures this runs
    // whenever a new app is selected (single or multi-website flow)
    useEffect(() => {
        const appKey = sessionStorage.getItem("app_key")
        if (!appKey) return

        const websites = JSON.parse(localStorage.getItem("websites") || "[]")
        const selectedApp = websites.find((w) => w.app_key === appKey)

        if (selectedApp) {
            applyViewRules(selectedApp)
        }
    }, []) // Empty deps is intentional: runs once after initial render
    // The applyViewRules call in Website/index.js is the primary trigger

    return (
        <ViewModeContext.Provider
            value={{
                viewMode,
                toggleViewMode,
                canToggle,
                applyViewRules,
            }}
        >
            {children}
        </ViewModeContext.Provider>
    )
}

export const useViewMode = () => useContext(ViewModeContext)